<!--
  HelpIcon.vue -- the small (?) beside a card title or major headline.

  Clicking it opens the existing help flyover scrolled to one section; the
  user stays on the page. For card headers and major headlines only -- not
  field labels or sub-headings.

  The target comes from PROPS, and must be STATIC LITERALS in the template:
  `components/Help/__tests__/help-icon-targets.spec.js` scans every usage and
  checks the article exists in docs/user/<audience>/ and the heading is a real
  anchor (derived from the markdown on disk). Renaming a heading in the docs
  without updating the icon fails that spec.

    audience  'owner' | 'shopper' | 'superadmin' -- which help tree.
    slug      article file name in docs/user/<audience>/ (no .md).
    heading   anchor id within the article (slugified heading text).
    label     optional accessible name; default "Help: <heading as words>".
    testid    optional data-testid; default help-icon-<slug>-<heading>.

  The icon is a Material ligature (`help_outline`), the app's convention and
  what the existing topbar Help buttons use.

  If the page's flyover is for a different audience than this icon's, the
  flyover ignores the target and opens route-contextually (audience safety,
  see router/helpContextMap.js).
-->
<template>
  <button
    type="button"
    class="help-icon"
    :data-testid="resolvedTestid"
    :aria-label="resolvedLabel"
    :title="resolvedLabel"
    :style="tapTargetStyle"
    @click.stop="open"
  >
    <span class="tf-icon help-icon__glyph" aria-hidden="true">help_outline</span>
  </button>
</template>

<script>
import { openHelpFlyoverAt } from "./useHelpFlyover";

const TAP_TARGET_PX = 24;

export default {
  name: "HelpIcon",

  props: {
    audience: {
      type: String,
      required: true,
      validator: (value) => ["owner", "shopper", "superadmin"].includes(value),
    },
    slug: { type: String, required: true },
    heading: { type: String, required: true },
    label: { type: String, default: "" },
    testid: { type: String, default: "" },
  },

  computed: {
    // 24px hit area (WCAG 2.2 AA 2.5.8) around a smaller, subtle glyph. Bound
    // inline (from one constant) so a test can assert it: jsdom does not
    // inject scoped SFC styles.
    tapTargetStyle() {
      return { minWidth: `${TAP_TARGET_PX}px`, minHeight: `${TAP_TARGET_PX}px` };
    },
    resolvedLabel() {
      return this.label || `Help: ${this.heading.replace(/-/g, " ")}`;
    },
    resolvedTestid() {
      return this.testid || `help-icon-${this.slug}-${this.heading}`;
    },
  },

  methods: {
    open() {
      openHelpFlyoverAt({
        audience: this.audience,
        slug: this.slug,
        heading: this.heading,
      });
    },
  },
};
</script>

<style scoped>
.help-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--tf-fg-4);
  cursor: pointer;
  vertical-align: middle;
  flex-shrink: 0;
}

.help-icon:hover {
  color: var(--tf-secondary);
  background: var(--tf-surface-dark);
}

.help-icon:focus-visible {
  outline: 2px solid var(--tf-secondary);
  outline-offset: 2px;
}

.help-icon__glyph {
  font-size: 17px;
}
</style>
